<template>
<div class="artifact-upload-modal" v-if="show">
    <div class="modal-overlay" @click="closeModal"></div>
    <div class="modal-container">
    <div class="modal-header">
        <h3><i class="fas fa-upload"></i> Upload Artifact Mask</h3>
        <button class="close-button" @click="closeModal">
        <i class="fas fa-times"></i>
        </button>
    </div>

    <div class="modal-body">
        <transition name="fading">
        <Loading v-if="isLoading"/>
        </transition>

        <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
        </div>
        <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
        </div>

        <form @submit.prevent="uploadArtifact" class="upload-form">
        <!-- Organization Selection -->
        <div class="form-group mb-3">
            <label for="organization" class="form-label">Organization</label>
            <select 
            id="organization" 
            v-model="selectedOrganization" 
            class="form-select"
            required
            >
            <option value="" disabled>Select an organization</option>
            <option 
                v-for="org in organizations" 
                :key="org.OrganizationUuid" 
                :value="org.OrganizationUuid"
            >
                {{ org.OrganizationName }}
            </option>
            </select>
        </div>

        <!-- Mask Type (Fixed to 'default' for now) -->
        <div class="form-group mb-3">
            <label for="maskType" class="form-label">Mask Type</label>
            <input 
            type="text" 
            id="maskType" 
            v-model="maskType" 
            class="form-control" 
            disabled
            />
            <small class="form-text text-muted">Currently only 'default' mask type is supported</small>
        </div>

        <!-- File Upload -->
        <div class="form-group mb-3">
            <label for="artifactFile" class="form-label">Artifact Mask Image</label>
            <div class="file-upload-container">
            <div 
                class="file-upload-area"
                :class="{ 'has-file': selectedFile, 'is-dragging': isDragging }"
                @click="triggerFileInput"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onFileDrop"
            >
                <div v-if="!selectedFile" class="upload-placeholder">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>Drag and drop your file here or click to browse</p>
                <p class="file-format">Supported formats: PNG, JPG, JPEG</p>
                </div>
                <div v-else class="file-preview">
                <img 
                    v-if="previewUrl" 
                    :src="previewUrl" 
                    alt="Preview" 
                    class="image-preview"
                />
                <div class="file-info">
                    <p class="file-name">{{ selectedFile.name }}</p>
                    <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
                </div>
                <button 
                    type="button" 
                    class="remove-file-btn" 
                    @click.stop="removeFile"
                >
                    <i class="fas fa-times"></i>
                </button>
                </div>
            </div>
            <input 
                type="file" 
                id="artifactFile" 
                ref="fileInput" 
                @change="onFileSelected" 
                accept="image/png,image/jpeg,image/jpg" 
                class="hidden-file-input"
            />
            </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
            <button 
            type="button" 
            class="btn btn-cancel"
            @click="closeModal"
            >
            Cancel
            </button>
            <button 
            type="submit" 
            class="btn btn-upload"
            :disabled="isUploading || !selectedFile || !selectedOrganization"
            >
            <span v-if="isUploading">
                <i class="fas fa-spinner fa-spin"></i> Uploading...
            </span>
            <span v-else>Upload Artifact Mask</span>
            </button>
        </div>
        </form>
    </div>
    </div>
</div>
</template>

<script>
import Loading from "@/components/Loading.vue";
import { uploadArtifactMask } from '@/services/artifactServices';
import { getAllOrg } from "@/services/orgServices";

export default {
name: 'ArtifactUploadModal',
components: {
    Loading
},
props: {
    show: {
    type: Boolean,
    default: false
    }
},
data() {
    return {
    organizations: [],
    selectedOrganization: '',
    maskType: 'default',
    selectedFile: null,
    previewUrl: '',
    isUploading: false,
    isLoading: false,
    successMessage: '',
    errorMessage: '',
    isDragging: false
    };
},
watch: {
    show(newVal) {
    if (newVal) {
        this.fetchOrganizations();
        // Reset form when modal is opened
        this.resetForm();
    }
    }
},
methods: {
    async fetchOrganizations() {
    this.isLoading = true;
    try {
        const result = await getAllOrg();
        if (result.data) {
        this.organizations = result.data;
        }
    } catch (error) {
        console.error('Error fetching organizations:', error);
        this.errorMessage = 'Failed to load organizations. Please try again.';
    } finally {
        this.isLoading = false;
    }
    },

    triggerFileInput() {
    this.$refs.fileInput.click();
    },

    onFileSelected(event) {
    const file = event.target.files[0];
    if (file) {
        this.handleFile(file);
    }
    },

    onFileDrop(event) {
    this.isDragging = false;
    const file = event.dataTransfer.files[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg')) {
        this.handleFile(file);
    } else {
        this.errorMessage = 'Please upload a valid image file (PNG, JPG, JPEG)';
    }
    },

    onDragOver() {
    this.isDragging = true;
    },

    onDragLeave() {
    this.isDragging = false;
    },

    handleFile(file) {
    this.selectedFile = file;
    
    // Create preview URL
    if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = URL.createObjectURL(file);
    
    // Clear any previous error messages
    this.errorMessage = '';
    },

    removeFile() {
    this.selectedFile = null;
    if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
    }
    this.$refs.fileInput.value = '';
    },

    formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async uploadArtifact() {
    if (!this.selectedOrganization || !this.selectedFile) {
        this.errorMessage = 'Please select an organization and upload a file';
        return;
    }

    try {
        this.isUploading = true;
        this.errorMessage = '';
        this.successMessage = '';

        await uploadArtifactMask(
        this.selectedOrganization,
        this.maskType,
        this.selectedFile
        );

        this.successMessage = 'Artifact mask uploaded successfully!';
        
        // Emit event to parent component to refresh the list
        this.$emit('upload-success');
        
        // Reset form after successful upload
        setTimeout(() => {
        this.resetForm();
        this.closeModal();
        }, 2000); // Close modal after 2 seconds
    } catch (error) {
        console.error('Error uploading artifact mask:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to upload artifact mask. Please try again.';
    } finally {
        this.isUploading = false;
    }
    },
    
    resetForm() {
    this.selectedOrganization = '';
    this.selectedFile = null;
    if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
    }
    if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
    }
    this.successMessage = '';
    this.errorMessage = '';
    },
    
    closeModal() {
    this.$emit('close');
    }
}
};
</script>

<style scoped>
.artifact-upload-modal {
position: fixed;
top: 0;
left: 0;
width: 100%;
height: 100%;
z-index: 1000;
display: flex;
align-items: center;
justify-content: center;
}

.modal-overlay {
position: absolute;
top: 0;
left: 0;
width: 100%;
height: 100%;
background-color: rgba(15, 23, 42, 0.38);
backdrop-filter: blur(8px);
}

.modal-container {
position: relative;
width: 90%;
max-width: 700px;
max-height: 90vh;
overflow-y: auto;
background-color: white;
border: 1px solid var(--ui-border);
border-radius: 14px;
box-shadow: 0 24px 80px rgba(15, 23, 42, 0.2);
z-index: 1001;
animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
from {
    opacity: 0;
    transform: translateY(20px);
}
to {
    opacity: 1;
    transform: translateY(0);
}
}

.modal-header {
display: flex;
justify-content: space-between;
align-items: center;
padding: 1.5rem;
border-bottom: 1px solid #e2e8f0;
background-color: #ffffff;
}

.modal-header h3 {
margin: 0;
color: #2d3748;
font-size: 1.25rem;
font-weight: 650;
}

.close-button {
background: none;
border: none;
font-size: 1.25rem;
color: #718096;
cursor: pointer;
transition: color 0.2s;
}

.close-button:hover {
color: #e53e3e;
}

.modal-body {
padding: 1.5rem;
position: relative;
}

.alert {
padding: 12px 16px;
border-radius: 9px;
margin-bottom: 20px;
}

.alert-success {
background-color: #f0fdf4;
border: 1px solid #bbf7d0;
color: #166534;
}

.alert-error {
background-color: #fef2f2;
border: 1px solid #fecaca;
color: #b91c1c;
}

.upload-form {
display: flex;
flex-direction: column;
gap: 20px;
}

.form-group {
display: flex;
flex-direction: column;
gap: 8px;
}

.form-label {
font-weight: 500;
color: #333;
font-size: 14px;
}

.form-select, .form-control {
border-radius: 9px;
border: 1px solid #e2e8f0;
padding: 0.6rem 1rem;
font-size: 0.9rem;
transition: all 0.2s;
}

.form-select:focus, .form-control:focus {
border-color: var(--primary-color);
box-shadow: 0 0 0 4px var(--ui-ring);
outline: none;
}

.file-upload-container {
width: 100%;
}

.file-upload-area {
border: 2px dashed #ddd;
border-radius: 10px;
padding: 30px;
text-align: center;
cursor: pointer;
transition: all 0.2s;
background-color: #f8fafc;
position: relative;
}

.file-upload-area:hover {
border-color: var(--primary-color);
background-color: #f8fafc;
}

.file-upload-area.has-file {
border-style: solid;
background-color: white;
}

.file-upload-area.is-dragging {
border-color: var(--primary-color);
background-color: #f0f7ff;
transform: scale(1.01);
}

.upload-placeholder {
display: flex;
flex-direction: column;
align-items: center;
gap: 10px;
}

.upload-placeholder i {
font-size: 40px;
color: var(--primary-color);
}

.upload-placeholder p {
margin: 0;
color: #666;
}

.file-format {
font-size: 12px;
color: #999;
}

.file-preview {
display: flex;
align-items: center;
gap: 15px;
}

.image-preview {
width: 80px;
height: 80px;
object-fit: cover;
border-radius: 10px;
border: 1px solid #e2e8f0;
}

.file-info {
flex: 1;
text-align: left;
}

.file-name {
font-weight: 500;
margin: 0 0 5px 0;
word-break: break-all;
}

.file-size {
color: #666;
font-size: 12px;
margin: 0;
}

.remove-file-btn {
background: none;
border: none;
color: #ff4d4f;
cursor: pointer;
font-size: 16px;
padding: 5px;
}

.hidden-file-input {
display: none;
}

.form-actions {
margin-top: 20px;
display: flex;
justify-content: flex-end;
gap: 12px;
}

.btn {
padding: 10px 20px;
border-radius: 9px;
font-weight: 650;
cursor: pointer;
transition: all 0.2s;
border: none;
font-size: 14px;
display: inline-flex;
align-items: center;
gap: 8px;
}

.btn-cancel {
background-color: #f1f5f9;
color: #64748b;
}

.btn-cancel:hover {
background-color: #e2e8f0;
}

.btn-upload {
background-color: var(--primary-color);
color: white;
}

.btn-upload:hover {
background-color: var(--primary-hover);
transform: translateY(-1px);
}

.btn:disabled {
opacity: 0.7;
cursor: not-allowed;
transform: none;
}

.form-text {
font-size: 12px;
color: #64748b;
}

/* Loading overlay */
.fading-enter-active, .fading-leave-active {
transition: opacity 0.3s;
}
.fading-enter-from, .fading-leave-to {
opacity: 0;
}

@media (max-width: 768px) {
.modal-container {
    width: 95%;
    max-height: 95vh;
}

.modal-header {
    padding: 1rem;
}

.modal-body {
    padding: 1rem;
}

.file-upload-area {
    padding: 20px;
}

.file-preview {
    flex-direction: column;
}

.image-preview {
    width: 100%;
    height: auto;
    max-height: 200px;
}

.file-info {
    width: 100%;
    text-align: center;
}

.form-actions {
    flex-direction: column-reverse;
}

.btn {
    width: 100%;
    justify-content: center;
}
}
</style>
  
  
